import { auth } from "../firebaseConfig";
import {
	AuditChange,
	AuditLogEntry,
	EntityType,
	OperationType
} from "../../models/auditLog";

const normalizeChangeValue = (value: unknown): unknown =>
	value === undefined ? null : value;

export const getChangeSnapshot = (
	before: Record<string, unknown> | null,
	after: Record<string, unknown> | null
): Record<string, AuditChange> | undefined => {
	if (!before || !after) {
		return undefined;
	}

	const changed: Record<string, AuditChange> = {};
	const keys = new Set<string>([
		...Object.keys(before),
		...Object.keys(after)
	]);

	keys.forEach((key) => {
		const beforeValue = before[key];
		const afterValue = after[key];
		const beforeJson = JSON.stringify(beforeValue);
		const afterJson = JSON.stringify(afterValue);

		if (beforeJson !== afterJson) {
			changed[key] = {
				before: normalizeChangeValue(beforeValue),
				after: normalizeChangeValue(afterValue)
			};
		}
	});

	return Object.keys(changed).length > 0 ? changed : undefined;
};

const createAuditEntry = ({
	entityType,
	entityId,
	operation,
	userId,
	username,
	changes,
	metadata
}: {
	entityType: EntityType;
	entityId: string;
	operation: OperationType;
	userId: string;
	username: string;
	changes?: Record<string, AuditChange>;
	metadata?: AuditLogEntry["metadata"];
}): Omit<AuditLogEntry, "id"> => {
	const entry: Omit<AuditLogEntry, "id"> = {
		entityType,
		entityId,
		operation,
		timestamp: new Date().toISOString(),
		userId,
		username
	};

	if (changes) {
		entry.changes = changes;
	}

	if (metadata && Object.keys(metadata).length > 0) {
		entry.metadata = metadata;
	}

	return entry;
};

const getCurrentUser = () => {
	return auth.currentUser;
};

/**
 * Builds an audit log entry from provided user, metadata, and explicit changes.
 * It does not compute diffs internally for the current wrapped-audit flow.
 */
export const buildAuditEntry = async ({
	entityType,
	entityId,
	operation,
	after,
	changes,
	metadata
}: {
	entityType: EntityType;
	entityId: string;
	operation: OperationType;
	after?: Record<string, unknown> | null;
	changes?: Record<string, AuditChange>;
	metadata?: AuditLogEntry["metadata"];
}): Promise<Omit<AuditLogEntry, "id">> => {
	const currentUser = getCurrentUser();

	const metaOut: AuditLogEntry["metadata"] = {
		...(metadata ?? {})
	};

	const afterObj = (after as Record<string, any>) ?? null;

	if (entityType === "Item") {
		metaOut.itemCode = metaOut.itemCode ?? afterObj?.itemCode ?? undefined;
		metaOut.itemName =
			metaOut.itemName ??
			afterObj?.name ??
			afterObj?.itemName ??
			undefined;
	}

	if (entityType === "Invoice") {
		metaOut.invoiceNumber =
			metaOut.invoiceNumber ?? afterObj?.invoiceNumber ?? undefined;
		metaOut.soldItems = metaOut.soldItems ?? afterObj?.items ?? undefined;
	}

	if (entityType === "Return") {
		metaOut.invoiceNumber =
			metaOut.invoiceNumber ?? afterObj?.invoiceNumber ?? undefined;
	}

	if (entityType === "CashMovement") {
		metaOut.movementType =
			metaOut.movementType ?? afterObj?.type ?? undefined;
		metaOut.amount = metaOut.amount ?? afterObj?.amount ?? undefined;
		metaOut.expenseCategory =
			metaOut.expenseCategory ??
			afterObj?.expenseCategory ??
			undefined;
		metaOut.personName =
			metaOut.personName ?? afterObj?.personName ?? undefined;
		metaOut.vendorName =
			metaOut.vendorName ?? afterObj?.vendorName ?? undefined;
	}

	const metaAny = metaOut as any;
	const userId =
		(currentUser && (currentUser.uid as string)) ??
		metaAny?.userId ??
		afterObj?.userId ??
		"unknown";
	const username =
		(currentUser && (currentUser.email || currentUser.displayName)) ??
		metaAny?.username ??
		afterObj?.username ??
		"Unknown";

	return createAuditEntry({
		entityType,
		entityId,
		operation,
		userId,
		username,
		changes,
		metadata: metaOut
	});
};
